import { Card, Dropdown, Field, Toggle } from '@/design-system';
import { REMINDER_DAYS, type TenantSettings, type TenantSettingsPatch } from '@/entities/tenant';

import { automationCopy, settingsCopy } from '../copy';

/** Waar "Factuur automatisch meesturen" staat: Berichten linkt hierheen. */
export const INVOICE_WITH_REPORT_ID = 'factuur-meesturen';

/** Waar "Herinneringen versturen" staat: Berichten linkt hierheen. */
export const REMINDERS_ID = 'herinneringen';

export interface AutomationCardProps {
    settings: TenantSettings;
    onChange: (patch: TenantSettingsPatch) => void;
    canManage: boolean;
    disabled?: boolean;
}

/**
 * Automatisering (scherm 11, linkerkolom).
 *
 * Alleen wat de API echt doet, en de regel onder elke schakelaar zegt wat er
 * gebeurt in de stand waarin hij nu staat:
 *
 * - `autoSendReport` betekent "sla het nalezen over": zodra een rapport klaar is,
 *   wordt het goedgekeurd - en goedkeuren ís versturen.
 * - `invoiceWithReport` laat een rapport dat zo vanzelf gaat de factuur meenemen, in
 *   dezelfde mail, met het standaard eigen bericht. Zonder automatisch versturen doet
 *   hij niets, en dat zegt de regel eronder dan ook.
 * - `autoInvoice` maakt na het versturen van het rapport een conceptfactuur.
 *   Uitgeven (nummeren en mailen) blijft een stap van de eigenaar.
 * - `sendReminders` stuurt de klant `reminderDaysBefore` dagen voor de afspraak
 *   een herinnering. Het moment kies je eronder; een waarde die buiten de vaste
 *   keuzes is gezet, blijft gewoon kiesbaar.
 *
 * De factuur-schakelaar en de herinneringen staan alleen hier. Berichten toont hun
 * stand met een link naar {@link INVOICE_WITH_REPORT_ID} en {@link REMINDERS_ID}.
 */
export function AutomationCard({ settings, onChange, canManage, disabled }: AutomationCardProps) {
    const locked = disabled || !canManage;
    const days = settings.reminderDaysBefore;
    const options = REMINDER_DAYS.includes(days as (typeof REMINDER_DAYS)[number])
        ? [...REMINDER_DAYS]
        : [...REMINDER_DAYS, days].sort((a, b) => a - b);

    return (
        <Card padding="lg" as="section" aria-labelledby="automation-title">
            <h2
                id="automation-title"
                className="mb-[6px] font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {automationCopy.title}
            </h2>

            <Toggle
                className="border-b border-[var(--border-subtle-2)]"
                label={automationCopy.autoSend}
                description={
                    settings.autoSendReport ? automationCopy.autoSendOn : automationCopy.autoSendOff
                }
                checked={settings.autoSendReport}
                disabled={locked}
                onCheckedChange={(checked) => onChange({ autoSendReport: checked })}
            />

            <Toggle
                id={INVOICE_WITH_REPORT_ID}
                className="border-b border-[var(--border-subtle-2)]"
                label={automationCopy.invoiceWithReport}
                description={
                    !settings.invoiceWithReport
                        ? automationCopy.invoiceWithReportOff
                        : settings.autoSendReport
                          ? automationCopy.invoiceWithReportOn
                          : automationCopy.invoiceWithReportManual
                }
                checked={settings.invoiceWithReport}
                disabled={locked}
                onCheckedChange={(checked) => onChange({ invoiceWithReport: checked })}
            />

            <Toggle
                className="border-b border-[var(--border-subtle-2)]"
                label={automationCopy.autoInvoice}
                description={
                    settings.autoInvoice
                        ? automationCopy.autoInvoiceOn
                        : automationCopy.autoInvoiceOff
                }
                checked={settings.autoInvoice}
                disabled={locked}
                onCheckedChange={(checked) => onChange({ autoInvoice: checked })}
            />

            <Toggle
                id={REMINDERS_ID}
                label={automationCopy.reminders}
                description={
                    settings.sendReminders
                        ? automationCopy.remindersOn(automationCopy.reminderMoment(days))
                        : automationCopy.remindersOff
                }
                checked={settings.sendReminders}
                disabled={locked}
                onCheckedChange={(checked) => onChange({ sendReminders: checked })}
            />

            {settings.sendReminders ? (
                <div className="pt-[6px]">
                    <Field label={automationCopy.reminderDays} size="md">
                        <Dropdown
                            value={String(days)}
                            disabled={locked}
                            options={options.map((option) => ({
                                value: String(option),
                                label: automationCopy.reminderOption(option),
                            }))}
                            onChange={(value) => onChange({ reminderDaysBefore: Number(value) })}
                        />
                    </Field>
                </div>
            ) : null}

            {canManage ? null : (
                <p className="mt-2 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {settingsCopy.readOnly}
                </p>
            )}
        </Card>
    );
}
